import { useTranslation } from "react-i18next";
import React, { useMemo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import BrandMark from '../components/BrandMark';

import i18n from "../i18n";
const tr = i18n.t.bind(i18n);

const GearIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ display: "block" }}
  >
    <path
      d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.4 15a1.8 1.8 0 0 0 .36 1.98l.05.05a2.2 2.2 0 0 1-1.56 3.76 2.2 2.2 0 0 1-1.56-.65l-.05-.05a1.8 1.8 0 0 0-1.98-.36 1.8 1.8 0 0 0-1.1 1.65V22a2.2 2.2 0 0 1-4.4 0v-.07a1.8 1.8 0 0 0-1.1-1.65 1.8 1.8 0 0 0-1.98.36l-.05.05a2.2 2.2 0 0 1-3.12 0 2.2 2.2 0 0 1 0-3.12l.05-.05A1.8 1.8 0 0 0 4.6 15a1.8 1.8 0 0 0-1.65-1.1H2.9a2.2 2.2 0 0 1 0-4.4h.05A1.8 1.8 0 0 0 4.6 8.4a1.8 1.8 0 0 0-.36-1.98l-.05-.05a2.2 2.2 0 0 1 3.12-3.12l.05.05A1.8 1.8 0 0 0 9.34 3.6a1.8 1.8 0 0 0 1.1-1.65V1.9a2.2 2.2 0 0 1 4.4 0v.05a1.8 1.8 0 0 0 1.1 1.65 1.8 1.8 0 0 0 1.98-.36l.05-.05a2.2 2.2 0 0 1 3.12 3.12l-.05.05A1.8 1.8 0 0 0 19.4 8.4a1.8 1.8 0 0 0 1.65 1.1h.05a2.2 2.2 0 0 1 0 4.4h-.05A1.8 1.8 0 0 0 19.4 15Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);


const Navbar = ({ user, onLogout, planPerms, onLanguageChange  }) => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 600);
  const [isMid, setIsMid]       = useState(() => window.innerWidth >= 600 && window.innerWidth < 1500);
  const [menuOpen, setMenuOpen] = useState(false);
  const [gearOpen, setGearOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation(); // Get the current path to highlight the selected tab

  const inPlan = (perm) => {
    if (!planPerms) return true;   // before load: don't hide yet
    return !!planPerms[perm];
  };

  const LANGS = [
    { code: "en", label: "English", flag: "/locales/en/flag.png" },
    { code: "el", label: "Ελληνικά", flag: "/locales/el/flag.png" },
  ];

  function LanguageSwitcher({ direction = "down", onChange }) {
    const { i18n } = useTranslation();
    const current = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

    const currentLang = useMemo(
      () => LANGS.find((l) => l.code === current) || LANGS[0],
      [current]
    );

    const [open, setOpen] = useState(false);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, minWidth: 120 });

    const preferUp = direction === "up" || isMid || isMobile;

    useEffect(() => {
      if (!open) return;

      const updateMenuPosition = () => {
        const btn = buttonRef.current;
        if (!btn) return;

        const rect = btn.getBoundingClientRect();
        const gap = 6;
        const estimatedMenuHeight = 96;

        let top = preferUp
          ? Math.max(8, rect.top - estimatedMenuHeight - gap)
          : Math.min(window.innerHeight - 8, rect.bottom + gap);

        const left = Math.max(8, rect.right - Math.max(rect.width, 120));

        setMenuStyle({
          top,
          left,
          minWidth: Math.max(rect.width, 120),
        });
      };

      const onDocMouseDown = (e) => {
        const target = e.target;
        if (buttonRef.current?.contains(target)) return;
        if (menuRef.current?.contains(target)) return;
        setOpen(false);
      };

      updateMenuPosition();
      document.addEventListener("mousedown", onDocMouseDown);
      window.addEventListener("resize", updateMenuPosition);
      window.addEventListener("scroll", updateMenuPosition, true);

      return () => {
        document.removeEventListener("mousedown", onDocMouseDown);
        window.removeEventListener("resize", updateMenuPosition);
        window.removeEventListener("scroll", updateMenuPosition, true);
      };
    }, [open, preferUp]);

    const setLang = async (lng) => {
      setOpen(false);
      if (onChange) return onChange(lng);
      return i18n.changeLanguage(lng);
    };

    return (
      <>
        <div style={{ position: "relative", display: "inline-block" }}>
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={open}
            title={`Language: ${currentLang.label}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "5px",
              border: "1px solid #ccc",
              borderRadius: 5,
              background: "#fff",
              color: "#111",
              cursor: "pointer",
            }}
          >
            <img
              src={currentLang.flag}
              alt={`${currentLang.label}`}
              width={20}
              height={14}
              style={{ display: "block", borderRadius: 2, objectFit: "cover" }}
            />
          </button>
        </div>

        {open && typeof document !== "undefined" &&
          createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{
                position: "fixed",
                left: menuStyle.left,
                top: menuStyle.top,
                border: "1px solid #ddd",
                borderRadius: 8,
                background: "#fff",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                overflow: "hidden",
                minWidth: menuStyle.minWidth,
                zIndex: 10000,
              }}
            >
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  role="menuitemradio"
                  aria-checked={l.code === current}
                  onClick={() => {
                    setLang(l.code);
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 10px",
                    border: 0,
                    background: l.code === current ? "#f5f5f5" : "#fff",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <img
                    src={l.flag}
                    alt={`${l.label}`}
                    width={20}
                    height={14}
                    style={{ display: "block", borderRadius: 2, objectFit: "cover" }}
                  />
                  <span style={{ fontSize: 13 }}>{l.label}</span>
                </button>
              ))}
            </div>,
            document.body
          )}
      </>
    );
  }

  const canSee = (perm) => {
    if (!user) return false;
    const hasUser = user.view_admin || user[perm];
    return hasUser && inPlan(perm);
  };

  const canSeeBilling = () => !!(user && (user.view_admin || user.view_billing));

  useEffect(() => {
    const checkMobile = () => {
      const w = window.innerWidth;
      setIsMobile(w < 600);
      setIsMid(w >= 600 && w < 1500);
    };
  
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = async () => {
    await onLogout(); // App.js will redirect
  };

  // All routes (desktop / tablet / full set)
  const linksAll = [
    {
      to: "/reservations-list",
      label: "Reservations",
      visible: canSee("view_reservations_list") || canSee("view_reservation_form"),
    },
    { to: "/reservation-form", label: "New Reservation", visible: canSee("view_reservation_form") },

    { to: "/bbq",      label: "BBQ",      visible: canSee("view_bbq") },
    { to: "/kitchen",  label: "Kitchen",  visible: canSee("view_kitchen") },
    { to: "/service",  label: "Service",  visible: canSee("view_service") },
    { to: "/schedule", label: "Schedule", visible: canSee("view_schedule") },

    { to: "/scheduler", label: "Scheduler", visible: canSee("view_scheduler") },
    { to: "/reports",   label: "Reports",   visible: canSee("view_reports") },
    { to: "/settings",  label: "Settings",  visible: canSee("view_settings") },
    { to: "/tables",    label: "Tables",    visible: canSee("view_tables") },
    { to: "/users",     label: "Users",     visible: canSee("view_users") },
    { to: "/clients",   label: "Clients",   visible: canSee("view_clients") },
    { to: "/billing",   label: "Billing",   visible: canSeeBilling() },
  ];

  // Phone menu: limited set for small width
  const linksPhone = [
    {
      to: "/reservations-list",
      label: "Reservations",
      visible: canSee("view_reservations_list") || canSee("view_reservation_form"),
    },
    { to: "/reservation-form", label: "New Reservation", visible: canSee("view_reservation_form") },
    { to: "/service",          label: "Service",         visible: canSee("view_service") },
    { to: "/schedule",         label: "Schedule",        visible: canSee("view_schedule") },
    { to: "/billing",          label: "Billing",         visible: canSeeBilling() },
  ];

  const linksPrimaryMid = [
    { to: "/reservations-list", label: "Reservations", visible: canSee("view_reservations_list") || canSee("view_reservation_form") },
    { to: "/reservation-form",  label: "New",          visible: canSee("view_reservation_form") },
    { to: "/bbq",               label: "BBQ",          visible: canSee("view_bbq") },
    { to: "/kitchen",           label: "Kitchen",      visible: canSee("view_kitchen") },
    { to: "/service",           label: "Service",      visible: canSee("view_service") },
    { to: "/schedule",          label: "Schedule",     visible: canSee("view_schedule") },
  ];

  const linksSecondaryMid = [
    { to: "/scheduler", label: "Scheduler", visible: canSee("view_scheduler") },
    { to: "/reports",   label: "Reports",   visible: canSee("view_reports") },
    { to: "/settings",  label: "Settings",  visible: canSee("view_settings") },
    { to: "/tables",    label: "Tables",    visible: canSee("view_tables") },
    { to: "/users",     label: "Users",     visible: canSee("view_users") },
    { to: "/clients",   label: "Clients",   visible: canSee("view_clients") },
    { to: "/billing",   label: "Billing",   visible: canSeeBilling() },
  ];

  useEffect(() => {
    // Only act on the root path
    if (location.pathname !== '/') return;

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    // Choose which link set to use depending on screen size
    let sourceLinks;
    if (isMobile) {
      // true phone layout
      sourceLinks = linksPhone;
    } else if (isMid) {
      // mid layout: any visible primary or secondary link is a valid landing
      sourceLinks = [...linksPrimaryMid, ...linksSecondaryMid];
    } else {
      // desktop layout
      sourceLinks = linksAll;
    }

    const firstVisible = sourceLinks.find(l => l.visible);
    navigate(firstVisible ? firstVisible.to : (canSeeBilling() ? '/billing' : '/login'), { replace: true });
  }, [user, isMobile, isMid, location.pathname, navigate, planPerms]);

  return (
    <>
      {isMobile ? (
        <div className="mobile-navbar">
          <div className="mobile-navbar-header">
            <div className="mobile-navbar-left" style={{gap: 0}}>
              <BrandMark size={28} href="/home" />
              {user && <span className="mobile-user">@{user.username}</span>}
            </div>
            {menuOpen && (<LanguageSwitcher  onChange={onLanguageChange} />)}
            <button className="mobile-menu-button" onClick={() => setMenuOpen(!menuOpen)}>{tr('☰')}</button>
          </div>

          {menuOpen && (
            <div className="mobile-navbar-menu">
              {linksPhone.map((link, index) =>
                link.visible ? (
                  <Link key={index} to={link.to} onClick={() => setMenuOpen(false)}>
                    {tr(link.label)}
                  </Link>
                ) : null
              )}
              <button onClick={handleLogout}>{tr('Logout')}</button>
            </div>
          )}
        </div>
      ) : isMid ? (
        <div className="mobile-navbar" style={{padding: 0}}>
          <div className="mobile-navbar-header">
             <h4 style={{ margin: 0 }}>
               <BrandMark size={28} href="/home" />
             </h4>
            {/* Mid range: primary links spread across width; gear before logout */}
            <div className="mobile-navbar-right" style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 12 }}>
              <div
                className="mid-links-spread"
                style={{
                  display: 'flex',
                  flex: 1,
                  justifyContent: 'space-evenly',
                  gap: 0,
                  alignItems: 'center',
                  minWidth: 0
                }}
              >
                {linksPrimaryMid.map((link, index) => {
                  if (!link.visible) return null;
                  const active = location.pathname === link.to;
                  return (
                    <Link
                      key={index}
                      to={link.to}
                      onClick={() => setGearOpen(false)}
                      style={{
                        textDecoration: 'none',
                        color: active ? '#007bff' : '#555',
                        fontWeight: active ? 'bold' : 'normal',
                        borderBottom: active ? '2px solid #007bff' : 'none',
                        paddingBottom: '5px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {tr(link.label)}
                    </Link>
                  );
                })}
              </div>
              {/* gear before logout */}
              <button
                className="mobile-menu-button"
                onClick={() => setGearOpen((o) => !o)}
                aria-label={tr("Open settings menu")}
                title={tr("Settings & More")}
                style={{
                  marginRight: 7,
              
                  width: 34,
                  height: 34,
                  padding: 0,
                  borderRadius: 10,
                  border: gearOpen ? "1px solid #9cc0ff" : "1px solid #d0d0d0",
                  background: gearOpen
                    ? "linear-gradient(180deg, #ffffff 0%, #eef5ff 100%)"
                    : "#fff",
                  boxShadow: gearOpen
                    ? "0 6px 14px rgba(0,0,0,0.10)"
                    : "0 2px 6px rgba(0,0,0,0.06)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: gearOpen ? "#0b57d0" : "#0b2e66",
                  cursor: "pointer",
                  transition:
                    "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease, background 120ms ease, color 120ms ease",
                }}
              >
                <GearIcon size={18} />
</button>

              <button
                onClick={handleLogout}
                style={{ padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px' }}
              >{tr('Logout:')}{' '}<b>{user.username || 'User'}</b>
              </button>
              <LanguageSwitcher onChange={onLanguageChange} />
            </div>

          </div>
          {gearOpen && (
            <div className="mobile-navbar-menu" style={{maxHeight: '46vh'}}>
              {linksSecondaryMid.map((link, index) =>
                link.visible ? (
                  <Link key={index} to={link.to} onClick={() => { setGearOpen(false); setMenuOpen(false); }}>
                    {tr(link.label)}
                  </Link>
                ) : null
              )}
            </div>
          )}         
        </div>
      ) : (
      
        <nav
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            zIndex: '3',
            padding: '10px',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #ccc'
          }}
        >
          
         <h3 style={{ margin: 0 }}>
           <BrandMark size={30} href="/home" />
         </h3>
        
        {/* Navigation Links */}
        <div style={{ display: 'flex', gap: '20px' }}>
          {(canSee("view_reservations_list") || canSee("view_reservation_form")) && (
            <Link
              to="/reservations-list"
              style={{
                textDecoration: 'none',
                color: location.pathname === '/reservations-list' ? '#007bff' : '#555',
                fontWeight: location.pathname === '/reservations-list' ? 'bold' : 'normal',
                borderBottom: location.pathname === '/reservations-list' ? '2px solid #007bff' : 'none',
                paddingBottom: '5px',
              }}
            >{tr('Reservations')}</Link>
          )}
        
          {canSee("view_reservation_form") && (
            <Link
              to="/reservation-form"
              style={{
                textDecoration: 'none',
                color: location.pathname === '/reservation-form' ? '#007bff' : '#555',
                fontWeight: location.pathname === '/reservation-form' ? 'bold' : 'normal',
                borderBottom: location.pathname === '/reservation-form' ? '2px solid #007bff' : 'none',
                paddingBottom: '5px',
              }}
            >{tr('New Reservation')}</Link>
          )}
        
          {canSee("view_bbq") && (
            <Link
              to="/bbq"
              style={{
                textDecoration: 'none',
                color: location.pathname === '/bbq' ? '#007bff' : '#555',
                fontWeight: location.pathname === '/bbq' ? 'bold' : 'normal',
                borderBottom: location.pathname === '/bbq' ? '2px solid #007bff' : 'none',
                paddingBottom: '5px',
              }}
            >{tr('BBQ')}</Link>
          )}
        
          {canSee("view_kitchen") && (
            <Link
              to="/kitchen"
              style={{
                textDecoration: 'none',
                color: location.pathname === '/kitchen' ? '#007bff' : '#555',
                fontWeight: location.pathname === '/kitchen' ? 'bold' : 'normal',
                borderBottom: location.pathname === '/kitchen' ? '2px solid #007bff' : 'none',
                paddingBottom: '5px',
              }}
            >{tr('Kitchen')}</Link>
          )}
        
          {canSee("view_service") && (
            <Link
              to="/service"
              style={{
                textDecoration: 'none',
                color: location.pathname === '/service' ? '#007bff' : '#555',
                fontWeight: location.pathname === '/service' ? 'bold' : 'normal',
                borderBottom: location.pathname === '/service' ? '2px solid #007bff' : 'none',
                paddingBottom: '5px',
              }}
            >{tr('Service')}</Link>
          )}
        
          {canSee("view_schedule") && (
            <Link
              to="/schedule"
              style={{
                textDecoration: 'none',
                color: location.pathname === '/schedule' ? '#007bff' : '#555',
                fontWeight: location.pathname === '/schedule' ? 'bold' : 'normal',
                borderBottom: location.pathname === '/schedule' ? '2px solid #007bff' : 'none',
                paddingBottom: '5px',
              }}
            >{tr('Schedule')}</Link>
          )}
        </div>

    
        {/* User Info and Logout */}
        <div style={{
          marginRight: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 10,
        }}>
          {user ? (
            <>
              {canSee("view_scheduler") && (
                <Link
                  to="/scheduler"
                  style={{
                    marginRight: '30px',
                    textDecoration: 'none',
                    color: location.pathname === '/scheduler' ? '#007bff' : '#555',
                    fontWeight: location.pathname === '/scheduler' ? 'bold' : 'normal',
                    borderBottom: location.pathname === '/scheduler' ? '2px solid #007bff' : 'none',
                    paddingBottom: '5px',
                  }}
                >{tr('Scheduler')}</Link>
              )}
        
              {canSee("view_reports") && (
                <Link
                  to="/reports"
                  style={{
                    marginRight: '30px',
                    textDecoration: 'none',
                    color: location.pathname === '/reports' ? '#007bff' : '#555',
                    fontWeight: location.pathname === '/reports' ? 'bold' : 'normal',
                    borderBottom: location.pathname === '/reports' ? '2px solid #007bff' : 'none',
                    paddingBottom: '5px',
                  }}
                >{tr('Reports')}</Link>
              )}
        
              {canSee("view_clients") && (
                <Link
                  to="/clients"
                  style={{
                    marginRight: '30px',
                    textDecoration: 'none',
                    color: location.pathname === '/clients' ? '#007bff' : '#555',
                    fontWeight: location.pathname === '/clients' ? 'bold' : 'normal',
                    borderBottom: location.pathname === '/clients' ? '2px solid #007bff' : 'none',
                    paddingBottom: '5px',
                  }}
                >{tr('Clients')}</Link>
              )}
        
              {canSee("view_settings") && (
                <Link
                  to="/settings"
                  style={{
                    marginRight: '30px',
                    textDecoration: 'none',
                    color: location.pathname === '/settings' ? '#007bff' : '#555',
                    fontWeight: location.pathname === '/settings' ? 'bold' : 'normal',
                    borderBottom: location.pathname === '/settings' ? '2px solid #007bff' : 'none',
                    paddingBottom: '5px',
                  }}
                >{tr('Settings')}</Link>
              )}
        
              {canSee("view_tables") && (
                <Link
                  to="/tables"
                  style={{
                    marginRight: '30px',
                    textDecoration: 'none',
                    color: location.pathname === '/tables' ? '#007bff' : '#555',
                    fontWeight: location.pathname === '/tables' ? 'bold' : 'normal',
                    borderBottom: location.pathname === '/tables' ? '2px solid #007bff' : 'none',
                    paddingBottom: '5px',
                  }}
                >{tr('Tables')}</Link>
              )}
        
              {canSee("view_users") && (
                <Link
                  to="/users"
                  style={{
                    marginRight: '30px',
                    textDecoration: 'none',
                    color: location.pathname === '/users' ? '#007bff' : '#555',
                    fontWeight: location.pathname === '/users' ? 'bold' : 'normal',
                    borderBottom: location.pathname === '/users' ? '2px solid #007bff' : 'none',
                    paddingBottom: '5px',
                  }}
                >{tr('Users')}</Link>
              )}
        
              {canSeeBilling() && (
                <Link
                  to="/billing"
                  style={{
                    marginRight: '30px',
                    textDecoration: 'none',
                    color: location.pathname === '/billing' ? '#007bff' : '#555',
                    fontWeight: location.pathname === '/billing' ? 'bold' : 'normal',
                    borderBottom: location.pathname === '/billing' ? '2px solid #007bff' : 'none',
                    paddingBottom: '5px',
                  }}
                >{tr('Billing')}</Link>
              )}
        
              <button
                onClick={handleLogout}
                style={{
                  padding: '5px 10px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >{tr('Logout:')}{' '}<b>{user.username || 'User'}</b>
              </button>
              
              <LanguageSwitcher onChange={onLanguageChange} />
            </>
          ) : (
            <Link
              to="/login"
              style={{
                padding: '5px 10px',
                backgroundColor: '#007bff',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '4px',
              }}
            >{tr('Login')}</Link>
          )}
        </div>

      </nav>
      )}
    </>
  );
};

export default Navbar;
